import { useTranslation } from 'react-i18next'
import {
  Box,
  IconComments,
  IconDelete,
  IconFileImport,
  IconGraphBar,
  IconRocket,
  IconSchedule,
  IconSearch,
  Skeleton,
  Stack,
  Text,
  TooltipRenderer,
  useToast
} from '@machobear/design-system'
import { useDialogController } from '@machobear/design-system'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { ChatIcon, ContractIcon } from '@/components/icons'
import Icon from '@/components/icon'
import DeleteConfirmationModal from '@/pages/Listing/CreateListingWizard/form-edit/delete-confirmation-modal'
import { ActionMenu } from './action-menu'
import { ActionMenuItem } from './action-menu-item'
import { useListingActions } from '../../../context/listing-actions'
import { useListingData } from '../../../context/listing-data'

export const ActionsSection = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const showToast = useToast()
  const history = useHistory()
  const {
    visible: deleteModalVisible,
    showModal: showDeleteModal,
    closeModal: closeDeleteModal
  } = useDialogController()
  const { listingPending, unreadCount, currentSponsorship, loading, listingId, listingAddress } =
    useListingData()

  const handleDelete = async () => {
    const { response } = await dispatch.listing.delete(listingId)
    if (response?.ok) {
      showToast({
        message: t('l.listing.edit.listing_deleted_toast.title', {
          address: listingAddress,
          defaultValue: '{{ address }} has been deleted!'
        }),
        description: t(
          'l.listing.edit.listing_deleted_toast.text',
          'If this was done in error, please contact us at support@liv.rent.'
        ),
        tone: 'positive'
      })
      history.push('/listings')
    }
  }

  const actions = useListingActions()
  if (loading)
    return (
      <Box borderRadius="standard" background="white" boxShadow="small">
        <ActionMenu title="">
          <Skeleton.Text lineCount={7} />
        </ActionMenu>
        <Box borderBottom="borderNeutralLight" style={{ height: 0 }} />
        <ActionMenu title="">
          <Skeleton.Text lineCount={3} />
        </ActionMenu>
      </Box>
    )

  const activeSponsorText = !!currentSponsorship ? 'Active' : ''

  return (
    <Box borderRadius="standard" background="white" boxShadow="small">
      <ActionMenu title={t('Manage')}>
        <ActionMenuItem
          icon={<IconComments />}
          text={t('Chats')}
          onClick={actions.goToChat}
          counter={unreadCount}
        />
        <ActionMenuItem
          icon={<IconRocket tone="formAccent" />}
          text={t('Sponsored listings')}
          onClick={actions.goToSponsored}
          badge={activeSponsorText}
        />
        <ActionMenuItem
          icon={<IconGraphBar />}
          text={t('Analytics')}
          onClick={actions.goToInsights}
        />
        <ActionMenuItem
          icon={<IconFileImport />}
          text={t('Applications')}
          onClick={actions.goToApplications}
        />

        {listingPending ? (
          <TooltipRenderer
            id="tooltip-disabled-contract-menu-item"
            placement="top"
            tooltip={
              <Stack space="xsmall">
                <Text size="standard" weight="strong">
                  {t('Your listing is still pending')}
                </Text>
                <Text size="small">{t('To continue, please publish your listing.')}</Text>
              </Stack>
            }
          >
            {({ triggerProps }) => (
              <Box {...triggerProps}>
                <ActionMenuItem
                  disabled
                  icon={<ContractIcon style={{ color: '#ABB3C1' }} />}
                  text={t('Contract')}
                  onClick={actions.goToContract}
                />
              </Box>
            )}
          </TooltipRenderer>
        ) : (
          <ActionMenuItem
            icon={<ContractIcon style={{ color: '#0E131B' }} />}
            text={t('Contract')}
            onClick={actions.goToContract}
          />
        )}

        <ActionMenuItem
          icon={<IconSchedule tone="formAccent" />}
          text={t('Showings')}
          onClick={actions.goToShowings}
        />
        <ActionMenuItem
          icon={<ChatIcon />}
          text={t('Auto-reply')}
          onClick={actions.goToAutoReply}
        />
      </ActionMenu>

      <Box borderBottom="borderNeutralLight" style={{ height: 0 }} />

      <ActionMenu title={t('Other')}>
        {listingPending ? (
          <TooltipRenderer
            id="tooltip-disabled-contract-menu-item"
            placement="top"
            tooltip={
              <Stack space="xsmall">
                <Text size="standard" weight="strong">
                  {t('Your listing is still pending')}
                </Text>
                <Text size="small">{t('To continue, please publish your listing.')}</Text>
              </Stack>
            }
          >
            {({ triggerProps }) => (
              <Box {...triggerProps}>
                <ActionMenuItem
                  disabled
                  icon={<IconSearch tone="disabled" />}
                  text={t('Suggested tenants')}
                  onClick={actions.goToSuggestedTenants}
                />
              </Box>
            )}
          </TooltipRenderer>
        ) : (
          <ActionMenuItem
            icon={<IconSearch tone="formAccent" />}
            text={t('Suggested tenants')}
            onClick={actions.goToSuggestedTenants}
          />
        )}
        <ActionMenuItem
          icon={<Icon id="notes" height={20} width={20} />}
          text={t('Notes')}
          onClick={actions.goToNotes}
        />
        <ActionMenuItem
          icon={<IconDelete tone="critical" />}
          text={t('Delete listing')}
          tone="critical"
          onClick={showDeleteModal}
        />
      </ActionMenu>
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        closeModal={closeDeleteModal}
        handleDelete={handleDelete}
      />
    </Box>
  )
}
